import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Edit, Trash2, Search, Eye, Building2 } from "lucide-react";
import {
  useBusinessesAdmin,
  useDeleteBusiness,
  type Business,
  type BusinessesQueryParams,
} from "../../../services/businessService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { hasPermission } from "../../../store/auth/authSlice";
import { Badge } from "../../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";

const Businesses: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<Business | null>(
    null
  );

  const queryParams: BusinessesQueryParams = {
    per_page: 10,
    page: currentPage,
    locale: currentLanguage,
    ...(searchTerm && { search: searchTerm }),
    ...(categoryFilter && { category: categoryFilter }),
    ...(verifiedFilter && { verified: verifiedFilter === "true" }),
  };

  const {
    data: businessesResponse,
    isLoading,
    error,
    refetch,
  } = useBusinessesAdmin(queryParams);

  const deleteBusinessMutation = useDeleteBusiness();

  const businesses: Business[] = businessesResponse?.data?.businesses || [];
  const pagination = businessesResponse?.data?.pagination;

  const handleDeleteBusiness = (business: Business) => {
    setBusinessToDelete(business);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteBusiness = async () => {
    if (!businessToDelete) return;

    try {
      await deleteBusinessMutation.mutateAsync(parseInt(businessToDelete.id));
      setIsDeleteDialogOpen(false);
      setBusinessToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete business:", error);
    }
  };

  const cancelDeleteBusiness = () => {
    setIsDeleteDialogOpen(false);
    setBusinessToDelete(null);
  };

  const handleEditBusiness = (business: Business) => {
    navigate(`/${currentLanguage}/admin/businesses/${business.id}/edit`);
  };

  const handleViewBusiness = (business: Business) => {
    navigate(`/${currentLanguage}/businesses/${business.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("sidebar.businesses")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(
                "admin.businesses.subtitle",
                "Manage and monitor all businesses"
              )}
            </p>
          </div>
          {hasPermission(user, "create businesses") && (
            <Link
              to={`/${currentLanguage}/admin/businesses/create`}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t("admin.businesses.createNew", "Create New Business")}
            </Link>
          )}
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={t(
                        "admin.businesses.searchPlaceholder",
                        "Search businesses..."
                      )}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <input
                    type="text"
                    placeholder={t(
                      "admin.businesses.categoryPlaceholder",
                      "Filter by category"
                    )}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div className="sm:w-48">
                  <select
                    value={verifiedFilter}
                    onChange={(e) => setVerifiedFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">{t("admin.businesses.all", "All")}</option>
                    <option value="true">
                      {t("admin.businesses.verified", "Verified")}
                    </option>
                    <option value="false">
                      {t("admin.businesses.unverified", "Unverified")}
                    </option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Businesses Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
                {t("admin.businesses.businessList", "Business List")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("common.loading", "Loading businesses...")}
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-20 px-4">
                  <p className="text-red-600 dark:text-red-400 text-lg font-semibold mb-2">
                    {t(
                      "admin.businesses.errorLoading",
                      "Error loading businesses"
                    )}
                  </p>
                </div>
              ) : businesses.length === 0 ? (
                <div className="text-center py-20 px-4">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
                    {t(
                      "admin.businesses.noBusinesses",
                      "No businesses found. Create your first business to get started."
                    )}
                  </p>
                  <Link
                    to={`/${currentLanguage}/admin/businesses/create`}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {t("admin.businesses.createNew", "Create New Business")}
                  </Link>
                </div>
              ) : (
                <>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.name", "Name")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.category", "Category")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.owner", "Owner")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.location", "Location")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.rating", "Rating")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.status", "Status")}
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.businesses.table.actions", "Actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {businesses.map((business) => (
                          <tr
                            key={business.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {business.image && (
                                  <img
                                    src={business.image}
                                    alt={business.name}
                                    className="w-10 h-10 rounded-full object-cover mr-3"
                                  />
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {business.name}
                                  </div>
                                  {business.featured && (
                                    <Badge className="mt-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                      {t(
                                        "admin.businesses.featured",
                                        "Featured"
                                      )}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {business.category || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {business.owner || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {business.location || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {(business.rating || 0).toFixed(1)}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                                  ({business.reviews || 0})
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {business.verified ? (
                                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800">
                                  {t("admin.businesses.verified", "Verified")}
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 border-gray-200 dark:border-gray-800">
                                  {t(
                                    "admin.businesses.unverified",
                                    "Unverified"
                                  )}
                                </Badge>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleViewBusiness(business)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                  title={t("admin.businesses.view", "View")}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {hasPermission(user, "edit businesses") && (
                                  <button
                                    onClick={() => handleEditBusiness(business)}
                                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                    title={t("admin.businesses.edit", "Edit")}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                )}
                                {hasPermission(user, "delete businesses") && (
                                  <button
                                    onClick={() =>
                                      handleDeleteBusiness(business)
                                    }
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    title={t(
                                      "admin.businesses.delete",
                                      "Delete"
                                    )}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-4 p-4">
                    {businesses.map((business) => (
                      <Card
                        key={business.id}
                        className="bg-white dark:bg-gray-800"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center flex-1">
                              {business.image && (
                                <img
                                  src={business.image}
                                  alt={business.name}
                                  className="w-12 h-12 rounded-full object-cover mr-3"
                                />
                              )}
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {business.name}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {business.category || "N/A"}
                                </p>
                              </div>
                            </div>
                            {business.verified ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                {t("admin.businesses.verified", "Verified")}
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300">
                                {t("admin.businesses.unverified", "Unverified")}
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-2 mb-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">
                                {t("admin.businesses.owner", "Owner")}:
                              </span>{" "}
                              {business.owner || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">
                                {t("admin.businesses.location", "Location")}:
                              </span>{" "}
                              {business.location || "N/A"}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <span className="font-medium">
                                {t("admin.businesses.rating", "Rating")}:
                              </span>{" "}
                              {(business.rating || 0).toFixed(1)} (
                              {business.reviews || 0}{" "}
                              {t("admin.businesses.reviews", "reviews")})
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewBusiness(business)}
                              className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              {t("admin.businesses.view", "View")}
                            </button>
                            {hasPermission(user, "edit businesses") && (
                              <button
                                onClick={() => handleEditBusiness(business)}
                                className="flex-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                              >
                                {t("admin.businesses.edit", "Edit")}
                              </button>
                            )}
                            {hasPermission(user, "delete businesses") && (
                              <button
                                onClick={() => handleDeleteBusiness(business)}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && pagination.last_page > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {t("common.page", "Page")} {pagination.current_page}{" "}
                        {t("common.of", "of")} {pagination.last_page}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={pagination.current_page === 1}
                          className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          {t("common.previous", "Previous")}
                        </button>
                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(pagination.last_page, prev + 1)
                            )
                          }
                          disabled={
                            pagination.current_page === pagination.last_page
                          }
                          className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          {t("common.next", "Next")}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("admin.businesses.deleteTitle", "Delete Business")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.businesses.deleteDescription",
                "Are you sure you want to delete this business? This action cannot be undone."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelDeleteBusiness}
              disabled={deleteBusinessMutation.isPending}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteBusiness}
              disabled={deleteBusinessMutation.isPending}
            >
              {deleteBusinessMutation.isPending
                ? t("common.deleting", "Deleting...")
                : t("admin.businesses.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Businesses;
